import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  // Tooltip,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Help as HelpIcon,
  Schedule as ScheduleIcon,
  // Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentService } from '@/services/assessment/assessmentService';
import type { AttemptResult } from '@/types/assessment';
// import jsPDF from 'jspdf';
// import moment from 'moment';
import {
  FacebookShareButton,
  LinkedinShareButton,
  FacebookIcon,
  LinkedinIcon,
} from 'react-share';

const AttemptDetailsPage: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [attemptResult, setAttemptResult] = useState<AttemptResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (attemptId) {
      fetchAttemptResult();
    }
  }, [attemptId]);

  const fetchAttemptResult = async () => {
    if (!attemptId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.getAttemptResult(attemptId);

      if (response.success && response.data) {
        setAttemptResult(response.data);
      } else {
        setError('Failed to fetch attempt details');
      }
    } catch (err) {
      setError('Error loading attempt details');
      console.error('Error fetching attempt result:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const toggleQuestionExpansion = (questionId: string) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const expandAllQuestions = () => {
    if (attemptResult) {
      setExpandedQuestions(new Set(attemptResult.questions.map(q => q.questionId)));
    }
  };

  const collapseAllQuestions = () => {
    setExpandedQuestions(new Set());
  };

  const getQuestionStatusIcon = (status: string) => {
    switch (status) {
      case 'correct':
        return <CheckCircleIcon color="success" />;
      case 'incorrect':
        return <CancelIcon color="error" />;
      case 'unanswered':
        return <HelpIcon color="warning" />;
      default:
        return <HelpIcon />;
    }
  };

  const getQuestionStatusColor = (status: string): 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'correct':
        return 'success';
      case 'incorrect':
        return 'error';
      case 'unanswered':
        return 'warning';
      default:
        return 'warning';
    }
  };

  const viewCertificate = () => {
    if (!attemptResult || !attemptResult.score.isPassed || !attemptId) return;
    
    const certificateUrl = `https://www.hrtalky.in/certificate/${attemptId}`;
    window.open(certificateUrl, '_blank');
  };

  const getCurrentUrl = () => {
    return `https://www.hrtalky.in/certificate/${attemptId}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getCurrentUrl());
      setSnackbarMessage('URL copied to clipboard!');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Failed to copy URL');
      setSnackbarOpen(true);
    }
  };

  // const shareOnWhatsAppw = () => {
  //   console.log('WhatsApp share clicked'); // Debug log
  //   const message = `🎉 ${attemptResult?.user?.name || 'I'} just completed the ${attemptResult?.assessment.title} assessment with a score of ${attemptResult?.score.percentage}%! Check out my certificate: ${getCurrentUrl()}`;
    
  //   // Try different WhatsApp URL formats
  //   const whatsappUrl1 = `whatsapp://send?text=${encodeURIComponent(message)}`;
  //   const whatsappUrl2 = `https://wa.me/?text=${encodeURIComponent(message)}`;
  //   const whatsappUrl3 = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    
  //   console.log('WhatsApp URL:', whatsappUrl2); // Debug log
    
  //   // Try mobile app first, then web
  //   const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
  //   if (isMobile) {
  //     // Try app first on mobile
  //     window.location.href = whatsappUrl1;
  //     // Fallback to web after 2 seconds if app doesn't open
  //     setTimeout(() => {
  //       window.open(whatsappUrl2, '_blank');
  //     }, 2000);
  //   } else {
  //     // Use web version on desktop
  //     window.open(whatsappUrl3, '_tab');
  //   }
  // };
const shareOnWhatsApp = () => {
  console.log('WhatsApp share clicked'); // Debug log
  const message = `www.hrtalky.in/certificate/${attemptId}`;
  
  const whatsappUrl1 = `whatsapp://send?text=${encodeURIComponent(message)}`;
  const whatsappUrl2 = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const whatsappUrl3 = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
  
  console.log('WhatsApp URL:', whatsappUrl2); // Debug log
  
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const openWhatsApp = (url:string, target = '_blank') => {
    const link = document.createElement('a');
    link.href = url;
    link.target = target;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isMobile) {
    // Try app first on mobile
    openWhatsApp(whatsappUrl1, '_self');
    // Fallback to web after 2 seconds if app doesn't open
    setTimeout(() => {
      openWhatsApp(whatsappUrl2);
    }, 2000);
  } else {
    // Use web version on desktop
    openWhatsApp(whatsappUrl3);
  }
};

  // const generateCertificate = async () => {
  //   if (!attemptResult || !attemptResult.score.isPassed) return;

  //   const doc = new jsPDF({
  //     orientation: 'portrait',
  //     unit: 'mm',
  //     format: 'a4',
  //     compress: true // Enable PDF compression
  //   });

  //   const pageWidth = 210;
  //   const pageHeight = 297;

  //   // Add Montserrat font support
  //   try {
  //     // For now, we'll use the Google Fonts Web API approach
  //     // In production, you would add the actual Montserrat font files
  //     // doc.addFont('path/to/Montserrat-Regular.ttf', 'Montserrat', 'normal');
  //     // doc.addFont('path/to/Montserrat-Bold.ttf', 'Montserrat', 'bold');
  //   } catch (error) {
  //     console.warn('Montserrat font not available, using helvetica fallback');
  //   }

  //   // Function to get the best available font (Montserrat preferred, helvetica fallback)
  //   const getFont = () => {
  //     // For now, return helvetica as jsPDF has better support
  //     // In production, you can check if Montserrat is loaded and return 'Montserrat' or 'helvetica'
  //     return 'helvetica'; // This will be the closest to Montserrat in jsPDF
  //   };

  //   // Function to get certificate background image based on difficulty
  //   const getCertificateBackground = (difficulty: string) => {
  //     switch (difficulty.toLowerCase()) {
  //       case 'beginner':
  //         return '/2.png';
  //       case 'intermediate':
  //         return '/3.png';
  //       case 'advanced':
  //         return '/3.png';
  //       default:
  //         return '/2.png';
  //     }
  //   };

  //   // Function to add text and logo overlays on the base image
  //   const addContentOverlays = () => {
  //     // Certificate body content - positioned slightly to the right
  //     const rightOffset = 20; // Move content 20mm to the right
      
  //     // Y position constants (moved up by 30mm from original positions)
  //     const Y_POSITIONS = {
  //       CERTIFY_TEXT: 110,      // was 120
  //       NAME: 135,             // was 145
  //       NAME_UNDERLINE: 140,   // was 150
  //       ACHIEVEMENT: 155,      // was 165
  //       COURSE_TITLE: 175,     // was 185
  //       SCORE: 190,            // was 200
  //       CERT_ID: 210           // was 220
  //     };
      
  //     // Date and certificate details at top
  //     const currentDate = moment().format('DD/MM/YYYY');

  //     const fontFamily = getFont();
      
  //     doc.setTextColor(60, 60, 60);
  //     doc.setFontSize(18);
  //     doc.setFont(fontFamily, 'normal');
  //     const certifyText = 'This is to certify that';
  //     const certifyWidth = doc.getTextWidth(certifyText);
  //     doc.text(certifyText, (pageWidth - certifyWidth) / 2 + rightOffset, Y_POSITIONS.CERTIFY_TEXT);

  //     // Recipient name
  //     doc.setFontSize(32);
  //     doc.setFont(fontFamily, 'bold');
  //     doc.setTextColor(40, 40, 40);
  //     const nameText = attemptResult?.user?.name || 'NA';
  //     const nameWidth = doc.getTextWidth(nameText);
  //     doc.text(nameText, (pageWidth - nameWidth) / 2 + rightOffset, Y_POSITIONS.NAME);

  //     // Elegant underline for name
  //     doc.setDrawColor(70, 130, 180);
  //     doc.setLineWidth(1);
  //     doc.line((pageWidth - nameWidth) / 2 + rightOffset - 15, Y_POSITIONS.NAME_UNDERLINE, (pageWidth - nameWidth) / 2 + rightOffset + nameWidth + 15, Y_POSITIONS.NAME_UNDERLINE);

  //     // Achievement description
  //     doc.setFontSize(14);
  //     doc.setFont(fontFamily, 'normal');
  //     doc.setTextColor(60, 60, 60);
  //     const achievementText = 'has successfully completed the assessment on, '+currentDate;
  //     const achievementWidth = doc.getTextWidth(achievementText);
  //     doc.text(achievementText, (pageWidth - achievementWidth) / 2 + rightOffset, Y_POSITIONS.ACHIEVEMENT);

  //     // Course title
  //     doc.setFontSize(18);
  //     doc.setFont(fontFamily, 'bold');
  //     doc.setTextColor(70, 130, 180);
  //     const courseTitle = `"${attemptResult.assessment.title}"`;
  //     const courseTitleWidth = doc.getTextWidth(courseTitle);
  //     doc.text(courseTitle, (pageWidth - courseTitleWidth) / 2 + rightOffset, Y_POSITIONS.COURSE_TITLE);

  //     // Score display
  //     doc.setFontSize(14);
  //     doc.setFont(fontFamily, 'normal');
  //     doc.setTextColor(60, 60, 60);
  //     const scoreText = `with a score of ${attemptResult.score.percentage}%`;
  //     const scoreWidth = doc.getTextWidth(scoreText);
  //     doc.text(scoreText, (pageWidth - scoreWidth) / 2 + rightOffset, Y_POSITIONS.SCORE);

  //     // Certificate ID below score - centered with right offset
  //     doc.setFont(fontFamily, 'normal');
  //     const certIdText = `Certificate ID: ${attemptId?.slice(-8) || 'CERT001'}`;
  //     const certIdWidth = doc.getTextWidth(certIdText);
  //     doc.text(certIdText, (pageWidth - certIdWidth) / 2 + rightOffset, Y_POSITIONS.CERT_ID);

  //     // Save the PDF after adding all content
  //     doc.save(`${attemptResult.assessment.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_certificate.pdf`);
  //   };

  //   // Load the base certificate template image
  //   const loadBaseImage = async () => {
  //     try {
  //       // Load the certificate template image
  //       const templateImg = new Image();
  //       templateImg.onload = () => {
  //         // Convert image to canvas with compression
  //         const canvas = document.createElement('canvas');
  //         const ctx = canvas.getContext('2d');
          
  //         // Reduce canvas size for compression while maintaining aspect ratio
  //         const maxWidth = 1240; // Reduce from original size for compression
  //         const maxHeight = 1754; // A4 portrait ratio
          
  //         let { width, height } = templateImg;
          
  //         // Calculate new dimensions maintaining aspect ratio
  //         if (width > maxWidth || height > maxHeight) {
  //           const ratio = Math.min(maxWidth / width, maxHeight / height);
  //           width = width * ratio;
  //           height = height * ratio;
  //         }
          
  //         canvas.width = width;
  //         canvas.height = height;
          
  //         // Draw image with better quality settings
  //         if (ctx) {
  //           ctx.imageSmoothingEnabled = true;
  //           ctx.imageSmoothingQuality = 'high';
  //           ctx.drawImage(templateImg, 0, 0, width, height);
  //         }
          
  //         // Convert to JPEG with compression (smaller file size than PNG)
  //         const templateData = canvas.toDataURL('image/JPEG', 0.85); // 85% quality for compression
          
  //         // Add the base template image to cover the entire page
  //         doc.addImage(templateData, 'JPEG', 0, 0, pageWidth, pageHeight);
          
  //         // Add text content overlays and difficulty badge
  //         addContentOverlays();
  //       };
        
  //       templateImg.onerror = () => {
  //         console.warn('Base template image failed to load, using fallback');
  //         // If template fails, continue with text overlays only
  //         addContentOverlays();
  //       };
        
  //       templateImg.src = getCertificateBackground(attemptResult.assessment.difficulty); // Load difficulty-specific certificate background
  //     } catch (error) {
  //       console.warn('Could not load base template:', error);
  //       addContentOverlays();
  //     }
  //   };

  //   // Start the process by loading the base image
  //   loadBaseImage();
  // };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !attemptResult) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Attempt not found'}</Alert>
        <Box mt={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/attempts')}>
            Back to Attempts
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
    }}>
      {/* Hero Header Section */}
      <Box
        sx={{
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
          px: 3,
          py: 4,
          borderRadius: 2
        }}
      >
        <Grid container spacing={2}  alignItems="center" mb={2} >
          {/* Header Content */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Box display="flex" alignItems="center">
              <Box
                // sx={{
                //   width: 56,
                //   height: 56,
                //   borderRadius: '16px',
                //   bgcolor: 'primary.main',
                //   display: 'flex',
                //   alignItems: 'center',
                //   justifyContent: 'center',
                //   mr: 3,
                //   boxShadow: 3,
                // }}
              >
               <IconButton
              onClick={() => navigate('/attempts')}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                mr: 2
              }}
            >
              <ArrowBackIcon />
            </IconButton>
              </Box>
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Attempt Details
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Review your assessment performance and answers
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          {/* Action Buttons Grid */}
          {attemptResult?.score.isPassed && (
            <Grid size={{ xs: 12, md: 5 }}>
             <Box sx={{
              display:'flex',
              justifyContent:'end'
             }}>
                <Button
                  variant="contained"
                  startIcon={<VisibilityIcon />}
                  onClick={viewCertificate}
                  sx={{ 
                    // textTransform: 'none',
                    // borderRadius: 2,
                    // px: 3,
                    // py: 1.5,
                    mr:2 ,
                    // fontWeight: 600,
                    // boxShadow: 2,
                    // bgcolor: 'primary.main',
                    // '&:hover': { 
                    //   boxShadow: 4,
                    //   bgcolor: 'primary.dark'
                    // }
                  }}
                >
                  View Certificate
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={() => setShareDialogOpen(true)}
                  sx={{ 
                    // textTransform: 'none',
                    // borderRadius: 2,
                    // px: 3,
                    // py: 1.5,
                    // fontWeight: 600,
                    // borderColor: 'info.main',
                    // color: 'info.main',
                    // '&:hover': { 
                    //   bgcolor: 'info.main',
                    //   color: 'white'
                    // }
                  }}
                >
                  Share Certificate
                </Button>
                </Box>
            </Grid>
          )}
        </Grid>
        
        {/* Assessment Info */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 12 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                p: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {attemptResult.assessment.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {attemptResult.assessment.description}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={attemptResult.assessment.difficulty}
                  color={attemptResult.assessment.difficulty === 'Beginner' ? 'success' : 
                         attemptResult.assessment.difficulty === 'Intermediate' ? 'warning' : 'error'}
                  sx={{ fontWeight: 500 }}
                />
                <Chip
                  label={`Attempt #${attemptResult.attempt.attemptNumber}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
                <Chip
                  label={attemptResult.summary.resultStatus}
                  color={attemptResult.score.isPassed ? 'success' : 'error'}
                  sx={{ fontWeight: 500 }}
                />
              </Stack>
            </Box>
          </Grid>
          {/* <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                p: 2.5,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 700,
                  color: attemptResult.score.isPassed ? 'success.main' : 'error.main',
                  mb: 0.5
                }}
              >
                {attemptResult.score.percentage}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Final Score
              </Typography>
            </Box>
          </Grid> */}
        </Grid>
      </Box>

      <Box sx={{ p: 3 }}>

      {/* Score Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{xs: 12, md: 4}}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h3" color={attemptResult.score.isPassed ? 'success.main' : 'error.main'}>
                {attemptResult.score.percentage}%
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Overall Score
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {attemptResult.score.correctAnswers}/{attemptResult.score.totalQuestions} correct
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs: 12, md: 4}}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4">
                {formatDuration(attemptResult.timing.totalTimeSpent)}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Time Spent
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg: {formatDuration(attemptResult.timing.averageTimePerQuestion)} per question
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs: 12, md: 4}}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SpeedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4">
                {attemptResult.score.accuracyPercentage}%
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Accuracy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {attemptResult.score.unansweredQuestions} unanswered
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Questions Review */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Questions Review ({attemptResult.questions.length} questions)
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={expandAllQuestions}
                sx={{ textTransform: 'none' }}
              >
                Expand All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={collapseAllQuestions}
                sx={{ textTransform: 'none' }}
              >
                Collapse All
              </Button>
            </Stack>
          </Box>
          
          <Stack spacing={2}>
            {attemptResult.questions.map((question) => {
              const isExpanded = expandedQuestions.has(question.questionId);
              
              return (
                <Card 
                  key={question.questionId} 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: 2,
                    '&:hover': { boxShadow: 2 },
                    transition: 'box-shadow 0.2s ease'
                  }}
                >
                  <CardContent sx={{ pb: 2 }}>
                    {/* Question Header - Always Visible */}
                    <Box 
                      display="flex" 
                      alignItems="flex-start" 
                      gap={2} 
                      sx={{ cursor: 'pointer' }}
                      onClick={() => toggleQuestionExpansion(question.questionId)}
                    >
                      <Box
                        sx={{
                          minWidth: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: question.result.status === 'correct' ? 'success.main' :
                                  question.result.status === 'incorrect' ? 'error.main' : 'warning.main',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}
                      >
                        {question.questionNumber}
                      </Box>
                      <Box flex={1}>
                        <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.5, mb: 1 }}>
                          {question.questionText}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Box display="flex" gap={1} alignItems="center">
                            <Chip
                              size="small"
                              icon={getQuestionStatusIcon(question.result.status)}
                              label={question.result.status === 'correct' ? 'Correct' : 
                                     question.result.status === 'incorrect' ? 'Incorrect' : 'Not Answered'}
                              color={getQuestionStatusColor(question.result.status)}
                              variant="filled"
                            />
                            {question.timeSpent > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {formatDuration(question.timeSpent)}
                              </Typography>
                            )}
                          </Box>
                          <IconButton size="small" sx={{ ml: 'auto' }}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Stack>
                      </Box>
                    </Box>

                    {/* Collapsible Content */}
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 3 }}>
                        {/* Answer Options */}
                  <Grid container spacing={2}>
                    {Object.entries(question.options).map(([optionKey, optionValue]) => {
                      const isCorrect = optionKey === question.correctAnswer.option;
                      const isUserAnswer = optionKey === question.userAnswer?.option;
                      const isWrongUserAnswer = isUserAnswer && !isCorrect;

                      return (
                        <Grid size={{xs: 12, sm: 6}} key={optionKey}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: isCorrect ? 'success.main' :
                                          isWrongUserAnswer ? 'error.main' : 'divider',
                              bgcolor: (theme) => isCorrect ? (theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(76, 175, 80, 0.1)') :
                                      isWrongUserAnswer ? (theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(244, 67, 54, 0.1)') : 
                                      theme.palette.background.default,
                              position: 'relative',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: isCorrect ? 'success.dark' :
                                            isWrongUserAnswer ? 'error.dark' : 'primary.main',
                                bgcolor: (theme) => isCorrect ? (theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.15)' : 'rgba(76, 175, 80, 0.15)') :
                                        isWrongUserAnswer ? (theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.15)' : 'rgba(244, 67, 54, 0.15)') : 
                                        theme.palette.action.hover
                              }
                            }}
                          >
                            <Box display="flex" alignItems="flex-start" gap={2}>
                              <Box
                                sx={{
                                  minWidth: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: isCorrect ? 'success.main' :
                                          isWrongUserAnswer ? 'error.main' : 'text.secondary',
                                  color: isCorrect || isWrongUserAnswer ? 'white' : 'background.paper',
                                  fontWeight: 'bold',
                                  fontSize: '0.8rem'
                                }}
                              >
                                {optionKey}
                              </Box>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  flex: 1, 
                                  // top:3,
                                  mt:.5,
                                  lineHeight: 1.4,
                                  color: 'text.primary',
                                  fontWeight: isCorrect || isUserAnswer ? 500 : 400
                                }}
                              >
                                {optionValue}
                              </Typography>
                            </Box>
                            
                            {/* Status indicators */}
                            {isCorrect && (
                              <Chip
                                size="small"
                                label="Correct Answer"
                                color="success"
                                sx={{ 
                                  position: 'absolute', 
                                  top: 8, 
                                  right: 8,
                                  fontSize: '0.7rem',
                                  height: 24
                                }}
                              />
                            )}
                            {isWrongUserAnswer && (
                              <Chip
                                size="small"
                                label="Your Answer"
                                color="error"
                                sx={{ 
                                  position: 'absolute', 
                                  top: 8, 
                                  right: 8,
                                  fontSize: '0.7rem',
                                  height: 24
                                }}
                              />
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Explanation */}
                  {question.explanation && (
                    <Box 
                      sx={{ 
                        mt: 3, 
                        p: 2.5, 
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        position: 'relative'
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 1.5, 
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main'
                          }}
                        />
                        Explanation
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.primary', 
                          lineHeight: 1.6,
                          fontStyle: 'italic'
                        }}
                      >
                        {question.explanation}
                      </Typography>
                    </Box>
                  )}

                  {/* Question Summary */}
                  <Box 
                    sx={{ 
                      mt: 2, 
                      pt: 2, 
                      borderTop: '1px solid', 
                      borderTopColor: 'divider' 
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid size={{xs: 6, sm: 4}}>
                        <Typography variant="caption" color="text.secondary">
                          Your Answer
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {question.userAnswer ? 
                            `${question.userAnswer.option}: ${question.userAnswer.text}` : 
                            'Not answered'
                          }
                        </Typography>
                      </Grid>
                      <Grid size={{xs: 6, sm: 4}}>
                        <Typography variant="caption" color="text.secondary">
                          Correct Answer
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.dark' }}>
                          {question.correctAnswer.option}: {question.correctAnswer.text}
                        </Typography>
                      </Grid>
                      {question.timeSpent > 0 && (
                        <Grid size={{xs: 12, sm: 4}}>
                          <Typography variant="caption" color="text.secondary">
                            Time Spent
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDuration(question.timeSpent)}
                          </Typography>
                        </Grid>
                      )}
                        </Grid>
                      </Box>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
      </Box>

      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box component="span" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
            Share Certificate
          </Box>
          <IconButton onClick={() => setShareDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Share your certificate achievement with your network
          </Typography>
          
          {/* Share URL Display */}
          {/* <Box 
            sx={{ 
              p: 2, 
              bgcolor: 'background.default', 
              borderRadius: 2, 
              mb: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Certificate URL
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Typography 
                variant="body2" 
                sx={{ 
                  flex: 1, 
                  wordBreak: 'break-all',
                  fontSize: '0.85rem'
                }}
              >
                {getCurrentUrl()}
              </Typography>
              <Tooltip title="Copy URL">
                <IconButton size="small" onClick={copyToClipboard}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box> */}

          {/* Social Media Share Buttons */}
          {/* <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Share on Social Media
          </Typography> */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4 }}>
              <FacebookShareButton
                url={getCurrentUrl()}
                hashtag="#certificate #achievement"
                style={{ width: '100%' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <FacebookIcon size={40} round />
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    Facebook
                  </Typography>
                </Box>
              </FacebookShareButton>
            </Grid>
            
            {/* <Grid size={{ xs: 6, sm: 4 }}>
              <TwitterShareButton
                url={getCurrentUrl()}
                title={`I just completed the ${attemptResult?.assessment.title} assessment with a score of ${attemptResult?.score.percentage}%! 🎉`}
                hashtags={['certificate', 'achievement', 'assessment']}
                style={{ width: '100%' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <TwitterIcon size={40} round />
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    Twitter
                  </Typography>
                </Box>
              </TwitterShareButton>
            </Grid> */}
            
            <Grid size={{ xs: 6, sm: 4 }}>
              <LinkedinShareButton
                url={getCurrentUrl()}
                title={`Certificate Achievement`}
                summary={`I successfully completed the ${attemptResult?.assessment.title} assessment with a score of ${attemptResult?.score.percentage}%!`}
                style={{ width: '100%' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <LinkedinIcon size={40} round />
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    LinkedIn
                  </Typography>
                </Box>
              </LinkedinShareButton>
            </Grid>
            
            <Grid size={{ xs: 6, sm: 4 }}>
              <Box
                onClick={shareOnWhatsApp}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderColor: 'primary.main'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: '#25D366',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <WhatsAppIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography variant="caption" sx={{ mt: 1 }}>
                  WhatsApp
                </Typography>
              </Box>
            </Grid>
            
            {/* Copy URL as a button option */}
            <Grid size={{ xs: 6, sm: 4 }}>
              <Box
                onClick={copyToClipboard}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderColor: 'primary.main'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ContentCopyIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="caption" sx={{ mt: 1 }}>
                  Copy URL
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AttemptDetailsPage;